import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TablePagination,
  TextField,
  Autocomplete,
  Avatar,
  Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ImagePicker from '../components/ImagePicker';

const API_URL = import.meta.env.VITE_API_URL;
const IMAGE_URL = import.meta.env.VITE_IMAGE_URL;

interface Product {
  productId: number;
  name: string;
  imageUrl: string;
}

interface Carousel {
  productsCarouselId: number;
  image: string;
  imageBigger: string;
}

const CarouselEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [carousel, setCarousel] = React.useState<Carousel | null>(null);
  const [products, setProducts] = React.useState<Product[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [total, setTotal] = React.useState(0);

  const [image, setImage] = React.useState<File | null>(null);
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);
  const [mediaId, setMediaId] = React.useState<number | null>(null);

  const [imageBigger, setImageBigger] = React.useState<File | null>(null);
  const [imageBiggerUrl, setImageBiggerUrl] = React.useState<string | null>(
    null,
  );
  const [mediaBiggerId, setMediaBiggerId] = React.useState<number | null>(null);

  const [searchOptions, setSearchOptions] = React.useState<Product[]>([]);
  const [searchInput, setSearchInput] = React.useState('');
  const [selectedProduct, setSelectedProduct] = React.useState<Product | null>(
    null,
  );
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [adding, setAdding] = React.useState(false);

  React.useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_URL}/carousel/${id}`)
      .then((res) => {
        setCarousel(res.data.carousel);
        setProducts(res.data.products);
        setTotal(res.data.products.length || 0);
        if (res.data.carousel.image)
          setImageUrl(`${IMAGE_URL}/${res.data.carousel.image}`);
        if (res.data.carousel.imageBigger)
          setImageBiggerUrl(`${IMAGE_URL}/${res.data.carousel.imageBigger}`);
      })
      .catch(() => alert('Не вдалося завантажити каталог'))
      .finally(() => setLoading(false));
  }, [id]);

  React.useEffect(() => {
    if (!searchInput.trim()) {
      setSearchOptions([]);
      return;
    }
    const timer = setTimeout(() => {
      setSearchLoading(true);
      axios
        .get(`${API_URL}/products`, {
          params: { search: searchInput, limit: 20 },
        })
        .then((res) => setSearchOptions(res.data.data))
        .catch(() => setSearchOptions([]))
        .finally(() => setSearchLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const alreadyInCarousel = selectedProduct
    ? products.some((p) => p.productId === selectedProduct.productId)
    : false;

  const handleAddProduct = async () => {
    if (!selectedProduct || !carousel) return;
    setAdding(true);
    try {
      await axios.post(
        `${API_URL}/carousel/${carousel.productsCarouselId}/products`,
        {
          productId: selectedProduct.productId,
        },
      );
      setProducts((prev) => [...prev, selectedProduct]);
      setTotal((prev) => prev + 1);
      setSelectedProduct(null);
      setSearchInput('');
    } catch {
      alert('Помилка при додаванні продукту');
    } finally {
      setAdding(false);
    }
  };

  const handleSave = async () => {
    try {
      const formData = new FormData();
      if (image) formData.append('image', image);
      if (mediaId) formData.append('mediaId', String(mediaId));
      if (imageBigger) formData.append('imageBigger', imageBigger);
      if (mediaBiggerId)
        formData.append('mediaBiggerId', String(mediaBiggerId));
      await axios.put(
        `${API_URL}/carousel/${carousel?.productsCarouselId}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      );
      navigate('/carousele');
    } catch {
      alert('Помилка при збереженні каталогу');
    }
  };

  const handleDeleteProduct = async (productId: number) => {
    if (!confirm('Видалити продукт з каталогу?')) return;
    try {
      await axios.delete(
        `${API_URL}/carousel/${carousel?.productsCarouselId}/products/${productId}`,
      );
      setProducts((prev) => prev.filter((p) => p.productId !== productId));
      setTotal((prev) => prev - 1);
    } catch {
      alert('Помилка при видаленні продукту з каталогу');
    }
  };

  if (loading) return <CircularProgress />;
  if (!carousel) return <Typography>Каталог не знайдений</Typography>;

  return (
    <Box sx={{ mx: 10, mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Редагування каталогу #{carousel.productsCarouselId}
      </Typography>

      <ImagePicker
        title="Зображення каталогу"
        value={imageUrl || ''}
        onChange={(value) => {
          if (value.type === 'file') {
            setImage(value.file);
            setMediaId(null);
            setImageUrl(URL.createObjectURL(value.file));
          }
          if (value.type === 'media') {
            setImage(null);
            setMediaId(value.mediaId);
            setImageUrl(value.url);
          }
        }}
      />

      <ImagePicker
        title="Зображення для більших цін"
        value={imageBiggerUrl || ''}
        onChange={(value) => {
          if (value.type === 'file') {
            setImageBigger(value.file);
            setMediaBiggerId(null);
            setImageBiggerUrl(URL.createObjectURL(value.file));
          }
          if (value.type === 'media') {
            setImageBigger(null);
            setMediaBiggerId(value.mediaId);
            setImageBiggerUrl(value.url);
          }
        }}
      />

      <Button variant="contained" sx={{ mt: 2, mb: 4 }} onClick={handleSave}>
        Зберегти каталог
      </Button>

      <Typography variant="h5" gutterBottom>
        Продукти каталогу
      </Typography>

      <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
        <Autocomplete
          sx={{ width: 400 }}
          options={searchOptions}
          loading={searchLoading}
          value={selectedProduct}
          inputValue={searchInput}
          onInputChange={(_, val) => setSearchInput(val)}
          onChange={(_, val) => setSelectedProduct(val)}
          getOptionLabel={(opt) => opt.name}
          isOptionEqualToValue={(opt, val) => opt.productId === val.productId}
          renderOption={(props, opt) => (
            <Box component="li" {...props} key={opt.productId}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar
                  src={`${IMAGE_URL}/${opt.imageUrl}`}
                  variant="rounded"
                  sx={{ width: 32, height: 32 }}
                />
                <Typography variant="body2">{opt.name}</Typography>
              </Stack>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Пошук продукту"
              placeholder="Введіть назву..."
            />
          )}
          noOptionsText={
            searchInput ? 'Нічого не знайдено' : 'Введіть назву для пошуку'
          }
        />

        <Stack spacing={0.5}>
          <Button
            variant="contained"
            disabled={!selectedProduct || alreadyInCarousel || adding}
            onClick={handleAddProduct}
            sx={{ height: 56 }}
          >
            Додати
          </Button>
          {alreadyInCarousel && (
            <Typography variant="caption" color="warning.main">
              Вже є в каталозі
            </Typography>
          )}
        </Stack>
      </Stack>

      {products.length > 0 ? (
        <Box>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Фото</TableCell>
                <TableCell>Назва</TableCell>
                <TableCell align="right">Дії</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((p) => (
                  <TableRow key={p.productId}>
                    <TableCell>
                      <img
                        src={`${IMAGE_URL}${p.imageUrl}`}
                        alt={p.name}
                        width={100}
                      />
                    </TableCell>
                    <TableCell>{p.name}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => navigate(`/products/${p.productId}`)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteProduct(p.productId)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 20, 50]}
          />
        </Box>
      ) : (
        <Typography>Продукти відсутні</Typography>
      )}
    </Box>
  );
};

export default CarouselEdit;
